import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/browser';
import { i18n } from 'i18next';
import * as process from 'process';
import React from 'react';
import { I18nextProvider } from 'react-i18next';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import store from '../configureStore';
import ErrorBoundary from '../ErrorBoundary';
import '../i18n';
import { loadFreightLanguageResources } from '../i18n';
import { addAuth0Context } from '../utilities/authUtils';
import * as firebaseUtils from '../utilities/firebaseUtils';
import './federated-app.scss';
import Router from './federated-router';

window['process'] = process;

const firebaseConfigProd = {
  apiKey: 'AIzaSyAnfAhdvyuTNmrhcik20-zsbplwkD2_jrI',
  authDomain: 'carrierpro-144918.firebaseapp.com',
  databaseURL: 'https://carrierpro-144918.firebaseio.com',
  projectId: 'carrierpro-144918',
  storageBucket: 'carrierpro-144918.appspot.com',
  messagingSenderId: '715693729194',
  appId: '1:715693729194:web:9ddbb35da0c9750d67b7d3',
  measurementId: 'G-Y1EFMEJ7NL',
};

const firebaseConfigDev = {
  apiKey: 'AIzaSyA54njeRl8HgA_1LQe9QRaCtESlr3DvzEo',
  authDomain: 'carrierpro-dev.firebaseapp.com',
  databaseURL: 'https://carrierpro-dev.firebaseio.com',
  projectId: 'carrierpro-dev',
  storageBucket: 'carrierpro-dev.appspot.com',
  messagingSenderId: '980554620681',
  appId: '1:980554620681:web:3d0bd3ae9a4ed2b8157dd8',
  measurementId: 'G-Y7W83YK56Q',
};

var environment = 'development';
try {
  environment = process.env.NODE_ENV || 'development';
} catch (e) {}

const isLocalhost =
  typeof globalThis.window !== 'undefined' &&
  (globalThis.window.location.hostname === 'localhost' ||
    globalThis.window.location.hostname === '127.0.0.1');

if (process.env.REACT_APP_SENTRY_DSN && !isLocalhost) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV,
  });
}

const queryClient = new QueryClient();

const FreightApp = ({
  i18n: i18nFromShell,
  header,
  highwayFlow,
}: {
  i18n: i18n;
  header?: React.JSX.Element;
  highwayFlow?: React.JSX.Element;
}) => {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isLanguageFilesLoaded, setIsLanguageFilesLoaded] =
    React.useState(false);
  const {
    isAuthenticated: isAuthenticatedAuth0,
    getAccessTokenSilently,
    user,
  } = useAuth0();

  React.useEffect(() => {
    addAuth0Context(getAccessTokenSilently, isAuthenticatedAuth0, user);
  }, [getAccessTokenSilently, isAuthenticatedAuth0, user]);

  React.useEffect(() => {
    if (!isLanguageFilesLoaded) {
      loadFreightLanguageResources(i18nFromShell);
    }

    const init = async () => {
      try {
        await firebaseUtils.init(
          environment === 'production' ? firebaseConfigProd : firebaseConfigDev
        );
      } catch (error) {
        console.error(error);
      } finally {
        setIsLanguageFilesLoaded(true);
        setIsLoaded(true);
      }
    };

    init();
  }, [i18nFromShell, isLanguageFilesLoaded]);

  return (
    <Provider store={store}>
      <ErrorBoundary>
        {isLoaded && (
          <I18nextProvider i18n={i18nFromShell}>
            <BrowserRouter>
              <QueryClientProvider client={queryClient}>
                <Router header={header} highwayFlow={highwayFlow} />
              </QueryClientProvider>
            </BrowserRouter>
          </I18nextProvider>
        )}
      </ErrorBoundary>
    </Provider>
  );
};

export default FreightApp;
